import { Save } from "@faire/slate-icons/Save";
import { Truck } from "@faire/slate-icons/Truck";
import { Button } from "@faire/slate/components/button";
import { ProgressBar } from "@faire/slate/components/progress-bar";
import React from "react";

import { type BrandCart, formatMoney, getCartSubtotal } from "./CartView";

interface SavedCartTileProps {
  cart: BrandCart;
  onViewCart: () => void;
}

export const SavedCartTile: React.FC<SavedCartTileProps> = ({
  cart,
  onViewCart,
}) => {
  const subtotal = getCartSubtotal(cart);
  const minimumMet = subtotal >= cart.minimumOrderDollars;
  const images = cart.products.map((product) => ({
    url: product.imageUrl,
    alt: product.name,
  }));

  return (
    <div
      className="border-fs-action-border-subdued relative w-full rounded-lg border border-dashed p-4 lg:p-6"
      style={{ borderColor: "var(--slate-color-action-border-subdued)" }}
    >
      {/* Saved for later pill */}
      <div className="bg-fs-surface-secondary text-fs-text-primary mb-4 inline-flex items-center gap-1 rounded-full px-2.5 py-1">
        <Save width="14" height="14" />
        <span className="type-fs-label-medium">Saved for later</span>
      </div>

      <div className="flex justify-between gap-3 lg:gap-8">
        {/* Left column: brand info + progress + actions */}
        <div className="flex w-full min-w-[204px] flex-1 flex-col gap-3 lg:max-w-96 lg:gap-4">
          <div className="flex items-center justify-between gap-1">
            <div className="flex min-w-0 flex-1 flex-row items-center gap-2">
              <BrandAvatar
                initial={cart.brandAvatarInitial}
                color={cart.brandAvatarColor}
              />
              <div className="min-w-0">
                <h5 className="type-fs-paragraph-medium truncate">
                  {cart.brandName}
                </h5>
                {cart.freeShipping ? (
                  <div className="text-fs-message-text-positive flex items-center gap-1">
                    <Truck width="14" height="14" />
                    <span className="type-fs-paragraph text-fs-message-text-positive">
                      {cart.freeShippingThreshold
                        ? `Free shipping over ${formatMoney(cart.freeShippingThreshold)}`
                        : "Free shipping"}
                    </span>
                  </div>
                ) : cart.isInsider ? (
                  <span className="type-fs-paragraph text-fs-text-subdued">
                    Insider
                  </span>
                ) : null}
              </div>
            </div>
            <div className="shrink-0">
              <p className="type-fs-paragraph-medium">
                {formatMoney(subtotal)}
              </p>
            </div>
          </div>

          <OrderMinimumProgressBar
            current={subtotal}
            minimum={cart.minimumOrderDollars}
          />

          <div className="flex w-full gap-2">
            <div className="flex-1">
              <Button
                variant="secondary"
                size="small"
                fullWidth
                onClick={onViewCart}
              >
                View cart
              </Button>
            </div>
            <div className="flex-1">
              <Button
                variant="primary"
                size="small"
                fullWidth
                disabled={!minimumMet}
              >
                Checkout
              </Button>
            </div>
          </div>
        </div>

        {/* Right column: product thumbnails (desktop) */}
        <div className="hidden min-w-0 items-center lg:flex">
          <BrandCartTileImageList
            images={images}
            totalProducts={cart.products.length}
          />
        </div>
      </div>
    </div>
  );
};

// ── Local visual replicas of CartView inline components ─────────────────────
// Kept visually identical to CartView's BrandAvatar / OrderMinimumProgressBar /
// BrandCartTileImageList so the saved-cart card matches the standard cart tile.

const BrandAvatar: React.FC<{ initial: string; color: string }> = ({
  initial,
  color,
}) => (
  <div
    className="flex shrink-0 items-center justify-center rounded-full"
    style={{ width: 32, height: 32, backgroundColor: color }}
  >
    <span
      className="text-fs-text-primary-inverse font-medium"
      style={{ fontSize: 32 * 0.4, lineHeight: 1 }}
    >
      {initial}
    </span>
  </div>
);

const OrderMinimumProgressBar: React.FC<{
  current: number;
  minimum: number;
}> = ({ current, minimum }) => {
  const met = current >= minimum;
  const progress = minimum === 0 ? 1 : Math.min(1, current / minimum);

  const progressText = (() => {
    if (minimum === 0) return "No minimum";
    if (met) return "Minimum reached";
    return `${formatMoney(minimum)} minimum · ${formatMoney(minimum - current)} to go`;
  })();

  return (
    <div className="w-full">
      <div className="flex flex-row items-center pb-1">
        <span className="type-fs-paragraph text-fs-text-primary">
          {progressText}
        </span>
      </div>
      <ProgressBar progress={progress} showIcon />
    </div>
  );
};

const BrandCartTileImageList: React.FC<{
  images: Array<{ url: string; alt: string }>;
  totalProducts: number;
}> = ({ images, totalProducts }) => {
  const renderImages = (count: number) => {
    const remaining = Math.max(0, totalProducts - count);
    return Array.from({ length: count }, (_, index) => {
      const image = images[index];
      const isLast = index === count - 1;
      return (
        <div key={index} className="relative max-h-36 w-36">
          {image ? (
            <img
              src={image.url}
              alt={image.alt}
              className="block h-full w-full object-cover"
              style={{ aspectRatio: "1" }}
            />
          ) : null}
          {isLast && remaining > 0 ? (
            <div className="absolute inset-0 flex items-center justify-center bg-(--slate-color-grey-1000)/40">
              <span className="type-fs-paragraph text-fs-text-primary-inverse font-medium">
                +{remaining}
              </span>
            </div>
          ) : null}
        </div>
      );
    });
  };

  return (
    <>
      <div className="hidden min-w-0 flex-row gap-2 min-[1280px]:hidden lg:flex">
        {renderImages(3)}
      </div>
      <div className="hidden min-w-0 flex-row gap-2 min-[1280px]:flex min-[1440px]:hidden">
        {renderImages(4)}
      </div>
      <div className="hidden min-w-0 flex-row gap-2 min-[1440px]:flex">
        {renderImages(5)}
      </div>
    </>
  );
};
